<template>
  <div class="production-log-tab">
    <!-- 툴바 -->
    <div class="log-toolbar">
      <el-button v-if="canEdit" type="primary" @click="showCreateDialog = true">
        + 보고등록
      </el-button>
      <el-button v-if="canEdit" @click="showDailyReportDialog = true">
        {{ dailyReports.length > 0 ? '일보 관리' : '일보 작성(수량)' }}
      </el-button>
      <el-radio-group v-model="typeFilter" size="small" @change="fetchLogs">
        <el-radio-button value="">전체</el-radio-button>
        <el-radio-button value="ISSUE">이슈</el-radio-button>
        <el-radio-button value="PROGRESS">진행</el-radio-button>
        <el-radio-button value="INSPECTION">검품</el-radio-button>
        <el-radio-button value="MATERIAL">자재</el-radio-button>
      </el-radio-group>
      <el-checkbox v-model="unresolvedOnly" @change="fetchLogs" label="미해결만" />
    </div>

    <!-- 생산일보 요약 -->
    <div v-if="latestReport" class="daily-report-summary">
      <el-card shadow="never" class="report-card">
        <div class="report-header">
          <span class="report-title">생산일보 — {{ latestReport.report_date }}</span>
          <el-button link type="primary" @click="showDailyReportDialog = true">
            {{ dailyReports.length > 0 ? '일보 관리' : '일보 작성' }}
          </el-button>
        </div>
        <div class="report-stats">
          <div class="stat">
            <span class="stat-label">금일</span>
            <span class="stat-value">{{ latestReport.daily_output?.toLocaleString() }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">누적</span>
            <span class="stat-value">{{ latestReport.cumulative_output?.toLocaleString() }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">목표</span>
            <span class="stat-value">{{ latestReport.target_output?.toLocaleString() }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">달성률</span>
            <span class="stat-value" :class="achievementClass">
              {{ latestReport.achievement_rate }}%
            </span>
          </div>
          <div class="stat">
            <span class="stat-label">불량</span>
            <span class="stat-value">{{ latestReport.defect_count }}</span>
          </div>
        </div>
        <el-progress
          :percentage="Math.min(latestReport.achievement_rate || 0, 100)"
          :stroke-width="8"
          :color="progressColor"
          :show-text="false"
          class="report-progress"
        />
      </el-card>
    </div>
    <div v-else-if="canEdit" class="no-report">
      <span class="no-report-hint">아직 생산일보가 없습니다 — 수량 실적은 [일보 작성(수량)]에서 입력합니다.</span>
    </div>

    <!-- 타임라인 -->
    <div v-loading="loading" class="log-timeline">
      <div v-if="logs.length === 0 && !loading" class="empty-state">
        <el-empty description="현장보고가 없습니다" />
      </div>

      <div v-for="(group, date) in groupedLogs" :key="date" class="date-group">
        <div class="date-header">{{ formatDate(date) }}</div>

        <div v-for="log in group" :key="log.id" class="log-item" :class="`severity-${log.severity.toLowerCase()}`">
          <div class="log-icon">
            {{ getLogIcon(log) }}
          </div>
          <div class="log-content">
            <div class="log-header-row">
              <el-tag :type="getSeverityType(log.severity)" size="small" effect="dark">
                {{ log.severity_display }}
              </el-tag>
              <el-tag size="small" effect="plain">
                {{ log.log_type_display }}
              </el-tag>
              <span class="log-title">{{ log.title }}</span>
              <span class="log-time">{{ formatTime(log.created_at) }}</span>
            </div>
            <div class="log-body">{{ log.content }}</div>

            <!-- 사진 -->
            <div v-if="log.photos?.length > 0" class="log-photos">
              <el-image
                v-for="photo in log.photos"
                :key="photo.id"
                :src="photo.image"
                :preview-src-list="log.photos.map(p => p.image)"
                fit="cover"
                class="photo-thumb"
              />
              <span class="photo-count">{{ log.photos.length }}장</span>
            </div>

            <!-- 위치 -->
            <div v-if="log.location" class="log-location">
              {{ log.location }}
            </div>

            <!-- 해결 상태 -->
            <div class="log-footer">
              <span class="log-author">{{ log.created_by_name }}</span>
              <template v-if="log.log_type === 'ISSUE'">
                <el-tag v-if="log.is_resolved" type="success" size="small">
                  해결완료 — {{ log.resolved_by_name }}
                </el-tag>
                <el-button
                  v-else-if="canEdit"
                  type="warning"
                  size="small"
                  @click="openResolveDialog(log)"
                >
                  해결 처리
                </el-button>
                <el-tag v-else type="danger" size="small">미해결</el-tag>
              </template>
            </div>

            <!-- 해결 내용 -->
            <div v-if="log.is_resolved && log.resolution_note" class="resolution-note">
              <strong>해결:</strong> {{ log.resolution_note }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 보고 등록 다이얼로그 -->
    <el-dialog v-model="showCreateDialog" title="현장보고 등록" width="600px" destroy-on-close>
      <el-form :model="createForm" label-width="80px">
        <el-form-item label="유형">
          <el-select v-model="createForm.log_type" style="width: 150px">
            <el-option label="이슈" value="ISSUE" />
            <el-option label="진행보고" value="PROGRESS" />
            <el-option label="검품" value="INSPECTION" />
            <el-option label="자재" value="MATERIAL" />
            <el-option label="일반" value="GENERAL" />
          </el-select>
          <el-select v-model="createForm.severity" style="width: 120px; margin-left: 10px">
            <el-option label="정보" value="INFO" />
            <el-option label="주의" value="WARNING" />
            <el-option label="긴급" value="CRITICAL" />
          </el-select>
        </el-form-item>
        <el-form-item label="제목" required>
          <el-input v-model="createForm.title" placeholder="보고 제목" />
        </el-form-item>
        <el-form-item label="내용">
          <el-input v-model="createForm.content" type="textarea" :rows="4" placeholder="상세 내용" />
        </el-form-item>
        <el-form-item label="현장위치">
          <el-input v-model="createForm.location" placeholder="예: 베트남 호치민 A공장 3라인" />
        </el-form-item>
        <el-form-item label="사진">
          <el-upload
            :file-list="uploadFiles"
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            list-type="picture-card"
            accept="image/*"
            :limit="5"
          >
            <el-icon><Plus /></el-icon>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">취소</el-button>
        <el-button type="primary" :loading="submitting" @click="submitLog">등록</el-button>
      </template>
    </el-dialog>

    <!-- 이슈 해결 다이얼로그 -->
    <el-dialog v-model="showResolveDialog" title="이슈 해결 처리" width="500px">
      <el-form>
        <el-form-item label="해결 내용">
          <el-input v-model="resolveNote" type="textarea" :rows="3" placeholder="해결 방법 및 결과를 입력하세요" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showResolveDialog = false">취소</el-button>
        <el-button type="primary" :loading="submitting" @click="resolveIssue">해결 완료</el-button>
      </template>
    </el-dialog>

    <!-- 생산일보 다이얼로그 -->
    <el-dialog v-model="showDailyReportDialog" title="생산일보 관리" width="700px" destroy-on-close>
      <DailyReportForm
        :main-request-id="mainRequestId"
        :daily-reports="dailyReports"
        @saved="handleReportSaved"
      />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import DailyReportForm from './DailyReportForm.vue'

const props = defineProps({
  mainRequestId: { type: [String, Number], required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(false)
const submitting = ref(false)
const logs = ref([])
const dailyReports = ref([])
const typeFilter = ref('')
const unresolvedOnly = ref(false)

// 다이얼로그
const showCreateDialog = ref(false)
const showResolveDialog = ref(false)
const showDailyReportDialog = ref(false)
const resolveNote = ref('')
const resolveTarget = ref(null)

// 등록 폼
const createForm = ref({
  log_type: 'ISSUE',
  severity: 'INFO',
  title: '',
  content: '',
  location: '',
})
const uploadFiles = ref([])

// 최신 일보
const latestReport = computed(() => dailyReports.value[0] || null)
const achievementClass = computed(() => {
  const rate = latestReport.value?.achievement_rate || 0
  if (rate >= 100) return 'rate-complete'
  if (rate >= 70) return 'rate-good'
  return 'rate-low'
})
const progressColor = computed(() => {
  const rate = latestReport.value?.achievement_rate || 0
  if (rate >= 100) return '#67C23A'
  if (rate >= 70) return '#409EFF'
  return '#E6A23C'
})

// 날짜별 그룹핑
const groupedLogs = computed(() => {
  const groups = {}
  for (const log of logs.value) {
    const date = log.created_at?.substring(0, 10) || 'unknown'
    if (!groups[date]) groups[date] = []
    groups[date].push(log)
  }
  return groups
})

const fetchLogs = async () => {
  // 상세 화면 이탈 시 route param 이 undefined 로 바뀌며 watch 가 발화 —
  // /main-requests/undefined/logs/ 404 방지 (2026-07-15)
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const params = {}
    if (typeFilter.value) params.log_type = typeFilter.value
    if (unresolvedOnly.value) params.unresolved = 'true'

    const response = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/logs/`,
      { params }
    )
    logs.value = response.data
  } catch (error) {
    console.error('Failed to fetch logs:', error)
  } finally {
    loading.value = false
  }
}

const fetchDailyReports = async () => {
  if (!props.mainRequestId) return
  try {
    const response = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/daily-reports/`
    )
    dailyReports.value = response.data
  } catch (error) {
    console.error('Failed to fetch daily reports:', error)
  }
}

const submitLog = async () => {
  if (!createForm.value.title.trim()) {
    ElMessage.warning('제목을 입력하세요.')
    return
  }

  submitting.value = true
  try {
    // 1. 보고 등록
    const response = await axiosInstance.post(
      `/production/main-requests/${props.mainRequestId}/logs/`,
      createForm.value
    )
    const logId = response.data.id

    // 2. 사진 업로드
    for (const file of uploadFiles.value) {
      const formData = new FormData()
      formData.append('image', file.raw)
      formData.append('caption', '')
      await axiosInstance.post(
        `/production/logs/${logId}/upload-photo/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    }

    ElMessage.success('현장보고가 등록되었습니다.')
    showCreateDialog.value = false
    resetCreateForm()
    fetchLogs()
  } catch (error) {
    ElMessage.error('보고 등록에 실패했습니다.')
    console.error(error)
  } finally {
    submitting.value = false
  }
}

const resetCreateForm = () => {
  createForm.value = {
    log_type: 'ISSUE',
    severity: 'INFO',
    title: '',
    content: '',
    location: '',
  }
  uploadFiles.value = []
}

const handleFileChange = (file, fileList) => {
  uploadFiles.value = fileList
}

const handleFileRemove = (file, fileList) => {
  uploadFiles.value = fileList
}

const openResolveDialog = (log) => {
  resolveTarget.value = log
  resolveNote.value = ''
  showResolveDialog.value = true
}

const resolveIssue = async () => {
  if (!resolveTarget.value) return
  submitting.value = true
  try {
    await axiosInstance.post(
      `/production/logs/${resolveTarget.value.id}/resolve/`,
      { resolution_note: resolveNote.value }
    )
    ElMessage.success('이슈가 해결 처리되었습니다.')
    showResolveDialog.value = false
    fetchLogs()
  } catch (error) {
    ElMessage.error('해결 처리에 실패했습니다.')
  } finally {
    submitting.value = false
  }
}

const handleReportSaved = () => {
  showDailyReportDialog.value = false
  fetchDailyReports()
}

// 헬퍼
const getLogIcon = (log) => {
  if (log.severity === 'CRITICAL') return '\uD83D\uDD34'
  if (log.severity === 'WARNING') return '\u26A0\uFE0F'
  const icons = { ISSUE: '\u274C', PROGRESS: '\uD83D\uDCCA', INSPECTION: '\uD83D\uDD0D', MATERIAL: '\uD83D\uDCE6', GENERAL: '\uD83D\uDCDD' }
  return icons[log.log_type] || '\uD83D\uDCDD'
}

const getSeverityType = (severity) => {
  return { CRITICAL: 'danger', WARNING: 'warning', INFO: 'info' }[severity] || 'info'
}

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'unknown') return ''
  const d = new Date(dateStr)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (d.toDateString() === today.toDateString()) return '오늘'
  if (d.toDateString() === yesterday.toDateString()) return '어제'
  return dateStr
}

const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return ''
  return dateTimeStr.substring(11, 16)
}

watch(() => props.mainRequestId, () => {
  fetchLogs()
  fetchDailyReports()
})

onMounted(() => {
  fetchLogs()
  fetchDailyReports()
})
</script>

<style scoped>
.production-log-tab {
  padding: 0;
}

.log-toolbar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* 생산일보 요약 */
.daily-report-summary {
  margin-bottom: 20px;
}

.report-card {
  background: #f8f9fa;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.report-title {
  font-weight: bold;
  font-size: 14px;
}

.report-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 10px;
}

.stat {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 11px;
  color: #909399;
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.rate-complete { color: #67C23A; }
.rate-good { color: #409EFF; }
.rate-low { color: #E6A23C; }

.report-progress {
  margin-top: 4px;
}

.no-report {
  text-align: center;
  padding: 10px;
  margin-bottom: 15px;
}

/* 타임라인 */
.date-group {
  margin-bottom: 24px;
}

.date-header {
  font-size: 13px;
  font-weight: bold;
  color: #606266;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 12px;
}

.log-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fafafa;
  border-left: 3px solid #dcdfe6;
}

.log-item.severity-critical {
  border-left-color: #F56C6C;
  background: #fef0f0;
}

.log-item.severity-warning {
  border-left-color: #E6A23C;
  background: #fdf6ec;
}

.log-icon {
  font-size: 20px;
  flex-shrink: 0;
  width: 28px;
  text-align: center;
}

.log-content {
  flex: 1;
  min-width: 0;
}

.log-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.log-title {
  font-weight: bold;
  font-size: 14px;
  flex: 1;
}

.log-time {
  font-size: 12px;
  color: #909399;
}

.log-body {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.log-photos {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.photo-thumb {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  cursor: pointer;
}

.photo-count {
  font-size: 12px;
  color: #909399;
}

.log-location {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.log-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.log-author {
  color: #909399;
}

.resolution-note {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9eb;
  border-radius: 4px;
  font-size: 13px;
  color: #67C23A;
}

.empty-state {
  padding: 40px 0;
}
.no-report-hint { font-size: 12px; color: #909399; }
</style>
